/**
 * Create Subscription Modal Component (September 2025)
 *
 * Modal for creating recurring credit subscriptions with customizable amounts and intervals.
 * Validates input and creates subscriptions via HTTP API with service key authentication.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { getConvexHttpUrl } from '../utils/convex';

interface CreateSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceKey: string;
  onSubscriptionCreated?: () => void;
}

interface CreateSubscriptionRequest {
  serviceApiKey: string;
  amount: number;
  validityDays: number;
  metadata?: Record<string, unknown>;
}

export function CreateSubscriptionModal({
  isOpen,
  onClose,
  serviceKey,
  onSubscriptionCreated,
}: CreateSubscriptionModalProps) {
  const [formData, setFormData] = useState({
    amount: 100,
    validityDays: 31,
    name: '',
    tier: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Create subscription mutation
  const createSubscriptionMutation = useMutation({
    mutationFn: async (data: CreateSubscriptionRequest) => {
      const response = await fetch(
        `${getConvexHttpUrl()}/api/subscriptions/start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `Failed to create subscription: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: () => {
      onSubscriptionCreated?.();
      handleClose();
    },
    onError: (error: Error) => {
      setErrors({ submit: error.message });
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.amount < 1) {
      newErrors.amount = 'Amount must be at least 1 credit';
    }
    if (formData.amount > 1000000) {
      newErrors.amount = 'Amount cannot exceed 1,000,000 credits';
    }

    if (formData.validityDays < 1) {
      newErrors.validityDays = 'Validity must be at least 1 day';
    }
    if (formData.validityDays > 365) {
      newErrors.validityDays = 'Validity cannot exceed 365 days';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const metadata: Record<string, unknown> = {};
    if (formData.name) metadata.name = formData.name;
    if (formData.tier) metadata.tier = formData.tier;
    metadata.created_via = 'dashboard';
    metadata.created_at = new Date().toISOString();

    createSubscriptionMutation.mutate({
      serviceApiKey: serviceKey,
      amount: formData.amount,
      validityDays: formData.validityDays,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    });
  };

  const handleClose = () => {
    if (!createSubscriptionMutation.isPending) {
      setFormData({ amount: 100, validityDays: 31, name: '', tier: '' });
      setErrors({});
      createSubscriptionMutation.reset();
      onClose();
    }
  };

  const presetOptions = [
    { amount: 50, days: 31, name: 'Basic Monthly', description: '50 credits/month' },
    { amount: 100, days: 31, name: 'Standard Monthly', description: '100 credits/month' },
    { amount: 250, days: 31, name: 'Pro Monthly', description: '250 credits/month' },
    { amount: 500, days: 31, name: 'Enterprise Monthly', description: '500 credits/month' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-slate-800 border border-cyan-500/30 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-cyan-400">Create Subscription</h2>
          <button
            onClick={handleClose}
            disabled={createSubscriptionMutation.isPending}
            className="text-slate-400 hover:text-slate-300 text-2xl leading-none disabled:opacity-50"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Error Display */}
          {errors.submit && (
            <div className="mb-6 bg-red-900/20 border border-red-500 rounded-lg p-4">
              <p className="text-red-400 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Preset Options */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-cyan-400 mb-3">
              Quick Presets
            </label>
            <div className="grid grid-cols-2 gap-3">
              {presetOptions.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, amount: preset.amount, validityDays: preset.days })
                  }
                  className="p-3 bg-slate-700 hover:bg-slate-600 border border-slate-600 hover:border-cyan-500 rounded-lg text-left transition-all"
                >
                  <div className="font-semibold text-cyan-400 text-sm">{preset.name}</div>
                  <div className="text-slate-400 text-xs">{preset.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Amount Input */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-cyan-400 mb-2">
              Credits Amount *
            </label>
            <input
              type="number"
              min="1"
              max="1000000"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseInt(e.target.value) || 0 })}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 text-slate-200 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors"
              placeholder="Enter credit amount"
            />
            {errors.amount && <p className="text-red-400 text-sm mt-2">{errors.amount}</p>}
            <p className="text-slate-400 text-xs mt-2">
              Number of credits to add each renewal cycle
            </p>
          </div>

          {/* Validity Days Input */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-cyan-400 mb-2">
              Renewal Interval (Days) *
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={formData.validityDays}
              onChange={(e) =>
                setFormData({ ...formData, validityDays: parseInt(e.target.value) || 0 })
              }
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 text-slate-200 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors"
              placeholder="Enter renewal interval"
            />
            {errors.validityDays && (
              <p className="text-red-400 text-sm mt-2">{errors.validityDays}</p>
            )}
            <p className="text-slate-400 text-xs mt-2">
              How often credits are added (1-365 days)
            </p>
          </div>

          {/* Optional Metadata */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-cyan-400 mb-2">
              Subscription Name (Optional)
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 text-slate-200 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors"
              placeholder="e.g., Monthly Pro Plan"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-cyan-400 mb-2">
              Tier (Optional)
            </label>
            <select
              value={formData.tier}
              onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 text-slate-200 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors"
            >
              <option value="">-- Select Tier --</option>
              <option value="basic">Basic</option>
              <option value="standard">Standard</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>

          {/* Preview */}
          <div className="mb-6 p-4 bg-slate-900 border border-slate-700 rounded-lg">
            <h3 className="text-sm font-semibold text-cyan-400 mb-3">Subscription Preview</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Credits per cycle:</span>
                <span className="text-slate-200 font-semibold">{formData.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Renewal interval:</span>
                <span className="text-slate-200 font-semibold">{formData.validityDays} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Credits per month:</span>
                <span className="text-cyan-400 font-semibold">
                  ~{Math.round((formData.amount / formData.validityDays) * 30)} credits
                </span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={createSubscriptionMutation.isPending}
              className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createSubscriptionMutation.isPending || !serviceKey}
              className="flex-1 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createSubscriptionMutation.isPending ? 'Creating...' : 'Create Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
